import { yupResolver } from '@hookform/resolvers/yup';
import { Box, LinearProgress, TextField } from '@material-ui/core';
import { Pagination, PaginationItem, PaginationRenderItemParams } from '@material-ui/lab';
import { Autocomplete, Button, Grid } from '@mui/material';
import classNames from 'classnames';
import { SGSCard, SGSForm, SGSFormInput, SGSFormLayout } from 'components';
import { useAppContext } from 'core/contexts';
import { affilieService } from 'core/services';
import { useSearchFormContext } from 'features/arretTravail/contexts/SearchFormProvider';
import { useFetchContractsPrev } from 'features/arretTravail/hooks/useFetchContractsPrev';
import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import stylesTables from '../../../../../components/SGSTable/SGSTable.module.scss';
import styles from '../../../css/ArretTravail.module.scss';
import AffiliateList from './AffiliateList';

const FormSearchAffilieContract: React.FC<Props> = ({}) => {
    const { user } = useAppContext();
    const history = useHistory();
    const { selectedSubscriber, searchParamsAffilie, setSearchParamsAffilie, selectedContract } = useSearchFormContext();
    const [affilies, setAffilies] = useState([]);
    const [totalPage, setTotalPage] = useState(0);
    const [submissionState, setSubmissionState] = useState({ isLoading: false, isSubmitted: false });
    const [subscriberOptions, setSubscriberOptions] = useState<{ value: string; label: string }[]>([]);
    const [collegeOptions, setCollegeOptions] = useState<{ value: string; label: string }[]>([]);
    const [typeContractOptions, setTypeContractOptions] = useState<{ value: string; label: string }[]>([]);
    const [contractsOptions, setContractsOptions] = useState<{ value: string; label: string }[]>([]);

    const { data: contracts, isLoading: isLoadingContracts } = useFetchContractsPrev(user);

    useEffect(() => {
        if (selectedSubscriber?.souscripteurCod) {
            const searchParams = searchParamsAffilie;
            searchParams.search.push({ souscripteurCod: selectedSubscriber.souscripteurCod });
            setSearchParamsAffilie(searchParams);
        }
    }, []);

    const methods = useForm<AffileSchema>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            souscripteurCod: selectedContract?.souscripteurCod || '',
            collegeCod: selectedContract?.idCodCollege || '',
            typeContratCod: selectedContract?.idContratType || '',
            contractCod: selectedContract?.cod || '',
        },
    });

    const {
        handleSubmit,
        control,
        watch,
        reset,
        formState: { isDirty },
    } = methods;

    const watchSubscriber = watch('souscripteurCod');
    const watchCollege = watch('collegeCod');
    const watchContractType = watch('typeContratCod');
    const watchContract = watch('contractCod');

    useEffect(() => {
        if (contracts) {
            const contractsPrev: Contract[] = contracts;

            // Filtering logic based on the selected options
            const getFilteredContracts = () => {
                return contractsPrev.filter((contract) => {
                    const matchesSubscriber = !watchSubscriber || contract.souscripteurCod === watchSubscriber;
                    const matchesCollege = !watchCollege || contract.idCodCollege === watchCollege;
                    const matchesContractType = !watchContractType || String(contract.codTypeContrat) === String(watchContractType);
                    const matchesContract = !watchContract || contract.cod === watchContract;

                    return matchesSubscriber && matchesCollege && matchesContractType && matchesContract;
                });
            };

            // Filtered contracts based on all current selections
            const filteredContracts = getFilteredContracts();

            // Set Subscriber Options
            const optionsSub = Array.from(
                new Map(
                    filteredContracts.map((contract) => [
                        contract.souscripteurCod,
                        {
                            value: contract.souscripteurCod,
                            label: contract.lbSouscripteur,
                        },
                    ]),
                ).values(),
            ).sort((a, b) => a.label.localeCompare(b.label));
            setSubscriberOptions(optionsSub);

            // Set College Options
            const optionsCollege = Array.from(
                new Map(
                    filteredContracts.map((contract) => [
                        contract.idCodCollege,
                        {
                            value: contract.idCodCollege,
                            label: contract.lbCollege,
                        },
                    ]),
                ).values(),
            ).sort((a, b) => a.label.localeCompare(b.label));
            setCollegeOptions(optionsCollege);

            // Set Type Contract Options
            const optionsTypeContract = Array.from(
                new Map(
                    filteredContracts.map((contract) => [
                        String(contract.codTypeContrat), // Ensure value is a string
                        {
                            value: String(contract.codTypeContrat),
                            label: contract.typeContrat,
                        },
                    ]),
                ).values(),
            ).sort((a, b) => a.label.localeCompare(b.label));
            setTypeContractOptions(optionsTypeContract);

            // Set Contracts Options
            const optionsContracts = Array.from(
                new Map(
                    filteredContracts.map((contract) => [
                        contract.cod,
                        {
                            value: contract.cod,
                            label: `${contract.lbContrat}`,
                        },
                    ]),
                ).values(),
            ).sort((a, b) => a.label.localeCompare(b.label));
            setContractsOptions(optionsContracts);
        }
    }, [contracts, watchSubscriber, watchCollege, watchContractType, watchContract]);

    const fetchAffilies = async () => {
        setSubmissionState({ isLoading: true, isSubmitted: false });

        const { page, pageSize, sortBy, search } = searchParamsAffilie;

        try {
            const result = await affilieService.getAffiliesPrev(page, pageSize, createSortPayload(sortBy), search);
            setAffilies(result.data);
            setTotalPage(result.totalPage); // Set total count for pagination
        } catch (error) {
            console.error('Error fetching affiliates:', error);
        } finally {
            setSubmissionState({ isLoading: false, isSubmitted: true });
        }
    };

    useEffect(() => {
        fetchAffilies();
    }, [searchParamsAffilie]);

    const handleSearchSubmit: SubmitHandler<AffileSchema> = (form) => {
        const search = Object.entries(form).reduce((acc, [key, value]) => {
            if (value !== null && value !== undefined && value.trim() !== '') {
                acc.push({ [key]: String(value).trim() });
            }
            return acc;
        }, [] as { [key: string]: string }[]);

        setSearchParamsAffilie((prev) => ({
            ...prev,
            search,
            page: 1, // Reset to first page on new search
        }));
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setSearchParamsAffilie((prev) => ({
            ...prev,
            page: newPage, // Material-UI's Pagination uses 1-based index
        }));
    };

    const handleCancel = () => {
        setAffilies([]); // Clear the search results
        reset({ souscripteurCod: '', collegeCod: '', typeContratCod: '', contractCod: '', nom: '', prenom: '', matricule: '', num_secu: '' });
        fetchAffilies();
    };

    const disableContract = () => {
        if (!watchContract) {
            return true;
        }
        return false;
    };

    const handleSelectedContract = () => {
        history.push(`/arrets/declare?codContrat=${watchContract}`);
    };

    return (
        <SGSCard>
            <FormProvider {...methods}>
                <SGSFormLayout onSubmit={handleSubmit(handleSearchSubmit)}>
                    <Grid container justifyContent="center">
                        <Grid container justifyContent="center">
                            <Grid item xs={12} md={6}>
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={2}>
                                    <Box textAlign="center" fontSize="14px" color="textSecondary">
                                        Si vous ne trouvez pas l'assuré via la recherche, vous pourrez le renseigner manuellement lors de la déclaration. Veuillez d'abord sélectionner un contrat dans
                                        le formulaire situé ci-dessous.
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={10}>
                            <Box className={classNames(styles.title, 'mb-4')}>Sélectionner un contrat </Box>

                            <SGSForm.Row>
                                <SGSForm.Col md={6}>
                                    <Controller
                                        name={'souscripteurCod'}
                                        control={control}
                                        render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
                                            <>
                                                {isLoadingContracts && <LinearProgress className={isLoadingContracts ? styles.showLoader : styles.hideLoader} />}
                                                <Autocomplete
                                                    id={'souscripteurCod'}
                                                    ref={ref}
                                                    multiple={false}
                                                    noOptionsText="Pas de résultat"
                                                    disableListWrap
                                                    fullWidth
                                                    options={subscriberOptions}
                                                    loading={isLoadingContracts}
                                                    renderInput={(params) => <TextField error={!!error} {...params} label={'Souscripteur'} helperText={error?.message} variant={'outlined'} />}
                                                    value={subscriberOptions.find((option) => option.value === value) || null}
                                                    onChange={(_, selectedOption) => {
                                                        onChange(selectedOption ? selectedOption?.value : null);
                                                    }}
                                                    getOptionLabel={(option) => {
                                                        if (typeof option === 'string') {
                                                            const selectedOption = subscriberOptions.find((sub) => sub.value === option);
                                                            return selectedOption?.label || '';
                                                        }
                                                        return option.label || '';
                                                    }}
                                                />
                                            </>
                                        )}
                                    />
                                </SGSForm.Col>
                                <SGSForm.Col md={6}>
                                    <Controller
                                        name={'collegeCod'}
                                        control={control}
                                        render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
                                            <>
                                                {isLoadingContracts && <LinearProgress className={isLoadingContracts ? styles.showLoader : styles.hideLoader} />}
                                                <Autocomplete
                                                    id={'collegeCod'}
                                                    ref={ref}
                                                    multiple={false}
                                                    noOptionsText="Pas de résultat"
                                                    disableListWrap
                                                    fullWidth
                                                    options={collegeOptions}
                                                    loading={isLoadingContracts}
                                                    renderInput={(params) => <TextField error={!!error} {...params} label={'Colleges'} helperText={error?.message} variant={'outlined'} />}
                                                    value={collegeOptions.find((option) => option.value === value) || null}
                                                    onChange={(_, selectedOption) => {
                                                        onChange(selectedOption ? selectedOption.value : null);
                                                    }}
                                                    getOptionLabel={(option) => {
                                                        if (typeof option === 'string') {
                                                            const selectedOption = collegeOptions.find((sub) => sub.value === option);
                                                            return selectedOption?.label || '';
                                                        }
                                                        return option.label || '';
                                                    }}
                                                />
                                            </>
                                        )}
                                    />
                                </SGSForm.Col>
                            </SGSForm.Row>
                            <SGSForm.Row>
                                <SGSForm.Col md={6}>
                                    <Controller
                                        name={'typeContratCod'}
                                        control={control}
                                        render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
                                            <>
                                                {isLoadingContracts && <LinearProgress className={isLoadingContracts ? styles.showLoader : styles.hideLoader} />}
                                                <Autocomplete
                                                    id={'typeContratCod'}
                                                    ref={ref}
                                                    multiple={false}
                                                    noOptionsText="Pas de résultat"
                                                    disableListWrap
                                                    fullWidth
                                                    options={typeContractOptions}
                                                    loading={isLoadingContracts}
                                                    renderInput={(params) => <TextField error={!!error} {...params} label={'Type contrat'} helperText={error?.message} variant={'outlined'} />}
                                                    value={typeContractOptions.find((option) => option.value === value) || null}
                                                    onChange={(_, selectedOption) => {
                                                        onChange(selectedOption ? selectedOption.value : null);
                                                    }}
                                                    getOptionLabel={(option) => {
                                                        if (typeof option === 'string') {
                                                            const selectedOption = typeContractOptions.find((sub) => sub.value === option);
                                                            return selectedOption?.label || '';
                                                        }
                                                        return option.label || '';
                                                    }}
                                                />
                                            </>
                                        )}
                                    />
                                </SGSForm.Col>
                                <SGSForm.Col md={6}>
                                    <Controller
                                        name={'contractCod'}
                                        control={control}
                                        render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
                                            <>
                                                {isLoadingContracts && <LinearProgress className={isLoadingContracts ? styles.showLoader : styles.hideLoader} />}
                                                <Autocomplete
                                                    id={'contractCod'}
                                                    ref={ref}
                                                    multiple={false}
                                                    noOptionsText="Pas de résultat"
                                                    disableListWrap
                                                    fullWidth
                                                    options={contractsOptions}
                                                    loading={isLoadingContracts}
                                                    renderInput={(params) => <TextField error={!!error} {...params} label={'Contrat'} helperText={error?.message} variant={'outlined'} />}
                                                    value={contractsOptions.find((option) => option.value === value) || null}
                                                    onChange={(_, selectedOption) => {
                                                        onChange(selectedOption ? selectedOption?.value : null);
                                                    }}
                                                    getOptionLabel={(option) => {
                                                        if (typeof option === 'string') {
                                                            const selectedOption = contractsOptions.find((sub) => sub.value === option);
                                                            return selectedOption?.label || '';
                                                        }
                                                        return option.label || '';
                                                    }}
                                                />
                                            </>
                                        )}
                                    />
                                </SGSForm.Col>
                            </SGSForm.Row>
                            <SGSForm.Row>
                                <SGSForm.Col>
                                    <Box display="flex" justifyContent="center">
                                        <Button type="button" variant="contained" color="primary" disabled={disableContract()} style={{ width: 'fit-content' }} onClick={handleSelectedContract}>
                                            SELECTIONNER CE CONTRAT SANS CHOISIR L'ASSURE
                                        </Button>
                                    </Box>
                                </SGSForm.Col>
                            </SGSForm.Row>
                            <Box className={classNames(styles.title, 'mb-4 mt-4')}>Rechercher un assuré</Box>
                            <SGSForm.Row>
                                <SGSForm.Col md={6}>
                                    <SGSFormInput variant="outlined" name="nom" label="Nom" />
                                </SGSForm.Col>
                                <SGSForm.Col md={6}>
                                    <SGSFormInput variant="outlined" name="prenom" label="Prénom" />
                                </SGSForm.Col>
                            </SGSForm.Row>
                            <SGSForm.Row>
                                <SGSForm.Col md={6}>
                                    <SGSFormInput variant="outlined" name="matriculeEntreprise" label="Matricule" />
                                </SGSForm.Col>
                                <SGSForm.Col md={6}>
                                    <SGSFormInput variant="outlined" name="numSecu" label="Numéro de sécu" />
                                </SGSForm.Col>
                            </SGSForm.Row>
                            <SGSForm.Row>
                                <SGSForm.Col>
                                    <Box display="flex" justifyContent="flex-end" alignItems="center">
                                        <Button id="cancel-search" type="button" variant="outlined" color="primary" onClick={handleCancel} style={{ marginRight: '10px' }}>
                                            ANNULER
                                        </Button>
                                        <Button id="search-subscriber" type="submit" variant="contained" color="primary" disabled={!isDirty}>
                                            RECHERCHER
                                        </Button>
                                    </Box>
                                </SGSForm.Col>
                            </SGSForm.Row>
                        </Grid>
                    </Grid>
                </SGSFormLayout>
            </FormProvider>

            {submissionState.isLoading && <Box style={{ textAlign: 'center' }}>Chargement...</Box>}
            {!submissionState.isLoading && submissionState.isSubmitted && (
                <Grid container justifyContent="center">
                    <Grid item xs={12} md={10}>
                        <Grid>
                            <Box className={classNames(styles.title, 'mb-4')}>Résultat de votre recherche</Box>
                            <Box style={{ margin: 'auto', marginTop: '20px' }}>
                                <AffiliateList affilies={affilies} />
                                <Box display="flex" justifyContent="center" mt={2}>
                                    {affilies.length > 0 ? (
                                        <Pagination
                                            count={totalPage}
                                            page={searchParamsAffilie.page}
                                            boundaryCount={3}
                                            siblingCount={2}
                                            shape="rounded"
                                            variant="outlined"
                                            onChange={(_event, value) => {
                                                handlePageChange(_event, value);
                                            }}
                                            classes={{ root: stylesTables.pagination, ul: stylesTables.list }}
                                            renderItem={(item: PaginationRenderItemParams) => (
                                                <PaginationItem
                                                    {...item}
                                                    classes={{
                                                        root: stylesTables.item,
                                                        page: stylesTables[item.type],
                                                        ellipsis: stylesTables.ellipsis,
                                                        selected: stylesTables.selected,
                                                        icon: stylesTables.icon,
                                                    }}
                                                />
                                            )}
                                        />
                                    ) : (
                                        <Grid container justifyContent="center">
                                            <Grid item xs={12} md={6}>
                                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={2}>
                                                    <Box fontSize="16px" fontWeight="bold" mb={1}>
                                                        Aucun résultat trouvé
                                                    </Box>
                                                    <Box textAlign="center" fontSize="14px" color="textSecondary">
                                                        Si vous ne trouvez pas l'assuré via la recherche, vous pourrez le renseigner manuellement lors de la déclaration. Veuillez d'abord sélectionner
                                                        un contrat dans le formulaire situé en haut de la page.
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </SGSCard>
    );
};

export default FormSearchAffilieContract;

interface Props {}

interface Contract {
    souscripteurCod: string;
    lbSouscripteur: string;
    [key: string]: any; // Optional, for additional unknown properties
}

interface AffileSchema {
    nom: string;
    prenom: string;
    num_secu: string;
    matricule: string;
    souscripteurCod: string;
    collegeCod: string;
    typeContratCod: string;
    contractCod: string;
}

const validationSchema: Yup.SchemaOf<AffileSchema> = Yup.object().shape({
    nom: Yup.string(),
    prenom: Yup.string(),
    num_secu: Yup.string(),
    matricule: Yup.string(),
    souscripteurCod: Yup.string().nullable(),
    collegeCod: Yup.string().nullable(),
    typeContratCod: Yup.string().nullable(),
    contractCod: Yup.string().nullable(),
});

const createSortPayload = (sortBy: { id: string; desc: boolean }[]): string => {
    if (sortBy.length === 0) return '';
    const { id, desc } = sortBy[0];
    return JSON.stringify({ column: id, order: desc ? 'DESC' : 'ASC' });
};
